// extensionDataBridge.js — capability-scoped data access for AI-generated
// workspace extensions (the /add-app self-serving builder).
//
// SECURITY MODEL (per Nika "make it safe and secure"): a generated extension
// renders in an ISOLATED iframe (createIframeApp) and must NOT get the raw
// Supabase client or the workspace's full SDK. Instead it gets a NARROWED `sdk`
// fetch-adapter executor that:
//
//   1. Only routes through `sdk.execute(from, op, args)` — the SDK transport,
//      which is workspace-pinned + RLS-enforced server-side (CLAUDE.md
//      "SDK-Only Backend Access"). No raw fetch / no Supabase client.
//   2. Rejects any entity (`from`) NOT in the extension's declared allow-list.
//      A build spec declares its data surface; anything off-list is denied so a
//      generated app can't read/write data it never asked for.
//   3. Forces every entity into the `workspaceProject.*` namespace — the only
//      namespace that is workspace-scoped. An extension can never reach
//      `organization.*`, `admin.*`, `auth.*`, `payment.*`, etc.
//   4. Allows only data ops (list/get/create/update/upsert/remove + subscribe).
//      Never rpc to arbitrary procedures, never auth/session ops.
//
// The result is a `fetch:` config (`{ adapter: 'sdk', executor }`) you hand to
// createIframeApp via `data.fetch`, so the extension's declarative
// `fetch: [{ from: 'policies', method: 'list' }]` declarations resolve through
// the scoped executor. Declared `from` values are bare entity names (e.g.
// 'policies'); the bridge namespaces them to `workspaceProject.policies`.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// Data ops an extension may invoke. Everything else (rpc, auth, custom procs)
// is denied. Mirrors the fetch plugin's verb set the sdk adapter supports.
//
// Named distinctly from `moduleDataBridge.js`'s identical-VALUE `ALLOWED_OPS`
// — two top-level consts sharing one name get esbuild-renamed in the merged
// bundle (`ALLOWED_OPS`/`ALLOWED_OPS2`), leaving this file's own read owned
// by no declaration frank's checker could attribute (correct by luck of
// file-processing order, not guaranteed). A locally-unique name removes the
// collision entirely.
const EXTENSION_ALLOWED_OPS = new Set([
  'list',
  'get',
  'create',
  'update',
  'upsert',
  'remove',
  'subscribe'
])

// Entities EVERY extension may read regardless of its declared surface — the
// shared, low-risk workspace context an extension commonly needs to render
// (the people directory for assignee pickers, the active workspace settings for
// theme, and `audience` — the members a page may notify by ref, as the server
// answers them: { ref, name, avatar }, no address, no id — SDK
// workspaceProject.audience → GET /notifications/audience). Read-only is
// enforced below for these.
const AMBIENT_READONLY_ENTITIES = new Set(['people', 'workspaceSettings', 'audience'])

// Real, server-backed workspaceProject.* entities an extension can persist
// against TODAY (writes durably land + survive reload), mirrored from the SDK
// EntityDispatcher's registered routes. A generated extension that manages
// tickets / documents / calendar / announcements / stories works end-to-end now.
//
// An extension MAY still declare a NEW entity (e.g. 'policies') — the bridge
// gates + namespaces it the same way — but until the server registers a
// matching workspaceProject.<name> route + table, its writes will reject with
// the dispatcher's "Unknown entity" error. `extensionEntitySupport()` below
// reports which declared entities are backed so the builder UI / cloud model
// can surface "this needs a backing store" instead of silently failing.
//
// NEW DOMAIN RECORD TYPES (e.g. insurance policies) need a generic
// workspace-scoped records table + a `workspaceProject.records` (or per-type)
// SDK entity on the SERVER — that's the one remaining cross-repo piece. The
// bridge is already shaped to scope to it the moment it exists.
//
// MAINTENANCE: this is a HAND-MIRRORED list — the SDK exposes no runtime entity
// registry to introspect, so it must be kept in sync by hand with the SDK's
// EntityDispatcher (`@symbo.ls/sdk` src/services/EntityDispatcher.js — the
// `workspaceProject.*` routes). Add an entry here when the server ships a new
// writable workspaceProject route; a missing entry only under-reports backing
// (an extension shows the "needs a store" notice for a route that does exist),
// it never grants unsafe access (the executor gate is independent of this list).
export const BACKED_RECORD_ENTITIES = new Set([
  // Generic record store (migration 0163, workspaceProject.records) — the
  // canonical home for AI-generated record types (policies, claims, …) keyed by
  // a `collection` namespace. New domain types persist here with no new table.
  'records',
  'tickets',
  'documents',
  'calendar',
  'announcements',
  'standups',
  'activity',
  'notifications'
])

// Strip the namespace if the model already wrote one, so 'workspaceProject.x',
// 'x', and a stray 'public.x' all normalize to the bare entity name we gate on.
const _bareEntity = (from) => {
  const s = String(from || '').trim()
  if (!s) return ''
  const dot = s.lastIndexOf('.')
  return (dot >= 0 ? s.slice(dot + 1) : s).toLowerCase()
}

// Normalize a declared-entities input to a deduped Set of bare names — the one
// place the raw input is processed; everything downstream uses the Set (O(1)
// membership, no per-call re-mapping).
const _toEntitySet = (declaredEntities) =>
  new Set(
    (Array.isArray(declaredEntities) ? declaredEntities : []).map(_bareEntity).filter(Boolean)
  )

// Build the scoped executor + the allow-list it enforces. `declaredEntities` is
// the extension's declared data surface (array of bare entity names). Returns
// `{ fetch, allowed, support }` — `fetch` goes onto createIframeApp's
// `data.fetch`. `getSdkFn` is injectable for tests; defaults to shared getSDK().
export const buildExtensionDataBridge = (declaredEntities = [], { getSdkFn = shellGetSDK } = {}) => {
  // Normalize ONCE. `declared` = what the extension explicitly asked for (write-
  // capable); `allowed` = declared + ambient read-only entities (always
  // reachable for reads so an app can render people/settings without declaring).
  const declared = _toEntitySet(declaredEntities)
  const allowed = new Set([...declared, ...AMBIENT_READONLY_ENTITIES])
  const WRITE_OPS = new Set(['create', 'update', 'upsert', 'remove'])

  const executor = (from, op, args, cb) => {
    const entity = _bareEntity(from)
    const operation = String(op || '').toLowerCase()

    if (!EXTENSION_ALLOWED_OPS.has(operation)) {
      return Promise.reject(new Error(`[extension] operation "${op}" is not permitted.`))
    }
    if (!entity || !allowed.has(entity)) {
      return Promise.reject(
        new Error(`[extension] entity "${from}" is not in this extension's declared data surface.`)
      )
    }
    // Ambient entities are READ-ONLY — a generated app can read the people
    // directory but never write to it unless it EXPLICITLY declared it (O(1)).
    if (
      WRITE_OPS.has(operation) &&
      AMBIENT_READONLY_ENTITIES.has(entity) &&
      !declared.has(entity)
    ) {
      return Promise.reject(new Error(`[extension] writes to "${entity}" are not permitted.`))
    }

    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk || typeof sdk.execute !== 'function') {
      return Promise.reject(new Error('[extension] SDK unavailable.'))
    }
    // Force the workspace-scoped namespace. This is the hard wall: an extension
    // can ONLY ever touch workspaceProject.* — never organization/admin/auth/…
    // The token this rides (sdk's _workspaceTokenProvider) reads the workspace
    // access token from storage with a fallback — no governance-federation
    // dependency (that plane is retired).
    return sdk.execute(`workspaceProject.${entity}`, operation, args, cb)
  }

  return {
    allowed: [...allowed],
    // Which declared entities are actually server-backed (writes persist) vs
    // not-yet-backed (gated + namespaced, but the dispatcher will reject until
    // the server registers the route). Lets the builder surface honest status.
    support: _entitySupport(declared),
    fetch: {
      adapter: 'sdk',
      executor
    }
  }
}

// Split a Set of bare entity names into { backed, unbacked } by backing status.
const _entitySupport = (declaredSet) => {
  const backed = []
  const unbacked = []
  for (const e of declaredSet) {
    if (BACKED_RECORD_ENTITIES.has(e) || AMBIENT_READONLY_ENTITIES.has(e)) backed.push(e)
    else unbacked.push(e)
  }
  return { backed, unbacked }
}

// Report backing status for an extension's declared entities. Returns
// { backed: [...], unbacked: [...] } — `backed` entities persist today;
// `unbacked` ones are gated + namespaced but need a server route/table before
// their writes land. Tolerates raw (un-normalized) input from external callers.
export const extensionEntitySupport = (declaredEntities = []) =>
  _entitySupport(_toEntitySet(declaredEntities))

// ── Static audit gate ────────────────────────────────────────────────────────
// Runtime safety net BEFORE a generated extension's DOMQL is eval'd + mounted.
// The cloud build agent SHOULD already constrain output, and the iframe realm
// isolates it — but defense-in-depth: reject source that reaches for raw
// network/storage/eval primitives instead of the scoped declarative `fetch:`
// surface. A generated extension has NO legitimate need for these; their
// presence means the model went off the rails (or the source was tampered
// with). Returns { ok: true } or { ok: false, error, violations: [...] }.
//
// This is a string/source scan, deliberately conservative: it can have false
// positives on legitimately-named props, so the patterns are tightened to the
// dangerous call forms (e.g. `fetch(` not the word "fetch", which the
// declarative `fetch:` array legitimately uses as a KEY, not a call).
const FORBIDDEN_PATTERNS = [
  { re: /\beval\s*\(/, label: 'eval()' },
  { re: /\bnew\s+Function\b/, label: 'new Function' },
  { re: /\bFunction\s*\(/, label: 'Function()' },
  { re: /\bfetch\s*\(/, label: 'raw fetch() — use the declarative fetch: array' },
  { re: /\bXMLHttpRequest\b/, label: 'XMLHttpRequest' },
  { re: /\bWebSocket\b/, label: 'WebSocket' },
  { re: /\bimport\s*\(/, label: 'dynamic import()' },
  { re: /\bdocument\s*\.\s*cookie\b/, label: 'document.cookie' },
  { re: /\blocalStorage\b/, label: 'localStorage' },
  { re: /\bsessionStorage\b/, label: 'sessionStorage' },
  { re: /\bindexedDB\b/, label: 'indexedDB' },
  { re: /\bnavigator\s*\.\s*sendBeacon\b/, label: 'navigator.sendBeacon' },
  // Reaching for the parent realm / shell root to escape the iframe sandbox.
  { re: /\bwindow\s*\.\s*(parent|top|opener)\b/, label: 'window.parent/top/opener' },
  { re: /\b__wsRoot\b/, label: '__wsRoot (shell escape)' },
  { re: /\b__addAppHost\b/, label: '__addAppHost (shell escape)' },
  { re: /\bpostMessage\s*\(/, label: 'postMessage' },
  // Direct DB/SDK reach-around the scoped bridge.
  { re: /\bcreateClient\s*\(/, label: 'createClient (raw Supabase)' },
  { re: /\bgetSupabase\b/, label: 'getSupabase' },
  { re: /\bel\s*\.\s*sdk\b/, label: 'el.sdk (use the declarative fetch: array)' }
]

export const auditExtensionSource = (source) => {
  const text = String(source || '')
  if (!text.trim()) return { ok: false, error: 'empty source', violations: ['empty'] }
  const violations = []
  for (const { re, label } of FORBIDDEN_PATTERNS) {
    if (re.test(text)) violations.push(label)
  }
  if (violations.length) {
    return {
      ok: false,
      error:
        'Generated extension uses unsafe patterns and was blocked: ' +
        violations.join(', ') +
        '. Extensions must use the declarative fetch: array for data and may not reach raw network/storage/eval.',
      violations
    }
  }
  return { ok: true }
}

// Pull the declared data surface off a build spec. The cloud build agent emits
// a spec; it MAY carry `entities: ['policies', …]` (the data surface it wrote
// fetch declarations against). Tolerant of several shapes so we don't depend on
// one exact contract from the server side.
export const declaredEntitiesFromSpec = (spec) => {
  if (!spec || typeof spec !== 'object') return []
  const raw =
    spec.entities || spec.dataEntities || spec.data?.entities || spec.capabilities?.entities || []
  if (!Array.isArray(raw)) return []
  // Accept ['policies'] or [{ name: 'policies' }] shapes.
  return raw
    .map((e) => (typeof e === 'string' ? e : e && (e.name || e.entity || e.from)))
    .filter(Boolean)
}
